"use client";

import { useState } from "react";
import Link from "next/link";
import { useGetHotArticles, useGetArticleCategories, useGetArticleList } from "@/lib/api/hooks";
import { useI18n, getLocalizedField } from "@/i18n";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const formatViewCount = (count) => {
  if (count == null) return "0";
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return String(count);
};

/* ---------- Skeleton components ---------- */

const FeaturedSkeleton = () => (
  <article className="blog-card" style={{ gridColumn: "span 2 / span 2" }}>
    <div className="blog-card-image" style={{ aspectRatio: "16/9", padding: 0 }}>
      <div className="skeleton sk-block" style={{ height: "100%", borderRadius: 0 }} />
    </div>
    <div className="blog-card-body">
      <div className="skeleton" style={{ width: 72, height: 22, marginBottom: 12 }} />
      <div className="skeleton sk-block" style={{ height: 30, marginBottom: 10, maxWidth: "85%" }} />
      <div className="skeleton sk-block" style={{ height: 16, marginBottom: 6 }} />
      <div className="skeleton sk-block" style={{ height: 16, marginBottom: 6, maxWidth: "70%" }} />
      <div className="skeleton sk-block" style={{ height: 16, marginBottom: 16, maxWidth: "40%" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
        <div className="skeleton" style={{ width: 40, height: 40, borderRadius: "50%" }} />
        <div style={{ flex: 1 }}>
          <div className="skeleton" style={{ width: 100, height: 14, marginBottom: 6 }} />
          <div className="skeleton" style={{ width: 80, height: 12 }} />
        </div>
        <div className="skeleton" style={{ width: 90, height: 14 }} />
      </div>
    </div>
  </article>
);

const SidebarSkeleton = () => (
  <div className="flex flex-col gap-4" style={{ height: "100%" }}>
    {[0, 1].map((i) => (
      <div key={i} className="blog-card" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div className="blog-card-image" style={{ aspectRatio: "16/7", flexShrink: 0, padding: 0 }}>
          <div className="skeleton sk-block" style={{ height: "100%", borderRadius: 0 }} />
        </div>
        <div className="blog-card-body" style={{ padding: "12px 16px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div className="skeleton" style={{ width: 60, height: 16, marginBottom: 8 }} />
          <div className="skeleton sk-block" style={{ height: 15, marginBottom: 6, maxWidth: "85%" }} />
          <div className="skeleton sk-block" style={{ height: 13, marginBottom: 3 }} />
          <div className="skeleton sk-block" style={{ height: 13, marginBottom: 10, maxWidth: "60%" }} />
          <div style={{ display: "flex", gap: 16, paddingTop: 2 }}>
            <div className="skeleton" style={{ width: 60, height: 12 }} />
            <div className="skeleton" style={{ width: 44, height: 12 }} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

const FilterBarSkeleton = () => (
  <div className="filter-bar" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)", paddingLeft: 20, paddingRight: 20, marginBottom: 24 }}>
    <span style={{ fontSize: 14, color: "var(--color-text-secondary)", marginRight: 8 }}>Category:</span>
    <span className="filter-chip active">All</span>
    {[60, 72, 64, 80].map((w, i) => (
      <div key={i} className="skeleton" style={{ width: w, height: 28, display: "inline-block", marginRight: 6, verticalAlign: "middle" }} />
    ))}
    <div className="filter-sort">
      <div className="skeleton" style={{ width: 72, height: 30 }} />
    </div>
  </div>
);

const ArticleCardSkeleton = () => (
  <div className="blog-card">
    <div className="blog-card-image" style={{ padding: 0 }}>
      <div className="skeleton sk-block" style={{ height: "100%", borderRadius: 0 }} />
    </div>
    <div className="blog-card-body">
      <div className="skeleton" style={{ width: 60, height: 16, marginBottom: 10 }} />
      <div className="skeleton sk-block" style={{ height: 18, marginBottom: 8, maxWidth: "85%" }} />
      <div className="skeleton sk-block" style={{ height: 14, marginBottom: 4 }} />
      <div className="skeleton sk-block" style={{ height: 14, marginBottom: 10, maxWidth: "65%" }} />
      <div style={{ display: "flex", gap: 16 }}>
        <div className="skeleton" style={{ width: 70, height: 12 }} />
        <div className="skeleton" style={{ width: 50, height: 12 }} />
      </div>
    </div>
  </div>
);

export default function BlogPage() {
  const { t, lang } = useI18n();
  const [categoryId, setCategoryId] = useState(null);
  const [sort, setSort] = useState("time");
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(9);

  const { data: hotData, isLoading: hotLoading } = useGetHotArticles();
  const { data: categoryData, isLoading: catLoading } = useGetArticleCategories();
  const { data: listData, isLoading: listLoading } = useGetArticleList({ categoryId, sort, pageNum, pageSize });

  const hotArticles = hotData?.data ?? hotData ?? [];
  const categories = categoryData?.data ?? categoryData ?? [];
  const records = listData?.data ?? [];
  const totalPages = listData?.pagination?.total ? Math.ceil(listData.pagination.total / pageSize) : 1;

  const featured = hotArticles[0];
  const sidebarArticles = hotArticles.slice(1);

  return (<>
    <div className="page-header" style={{ background: "linear-gradient(180deg, rgba(8, 15, 28, 0.60) 0%, rgba(8, 15, 28, 0.35) 45%, rgba(8, 15, 28, 0.55) 100%), url('/assets/blog-header.jpg') center/cover no-repeat" }}>
      <div className="container">
        <div className="breadcrumb">
          <Link href="/">{t("common.home")}</Link>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="m9 18 6-6-6-6"/></svg>
          <span>{t("nav.blog")}</span>
        </div>
        <h1 className="page-title">{t("blog.pageTitle")}</h1>
        <p className="page-subtitle">{t("blog.pageSubtitle")}</p>
      </div>
    </div>

    {/* ---------- Hot Articles ---------- */}
    <section className="section" style={{ paddingBottom: 0}}>
      <div className="container">
        {hotLoading ? (
          <div className="grid grid-cols-3" style={{ gap: "var(--space-5)" }}>
            <FeaturedSkeleton />
            <SidebarSkeleton />
          </div>
        ) : (
          <div className="grid grid-cols-3" style={{ gap: "var(--space-5)" }}>
            <Link href={featured ? `/blog/${featured.id}` : "/blog"} style={{ gridColumn: "span 2 / span 2", textDecoration: "none" }}>
              <article className="blog-card">
                <div className="blog-card-image" style={{ aspectRatio: "16/9" }}>
                  <img src={featured?.coverImage || "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200&q=80"} alt={getLocalizedField(featured, "title", lang) || ""}/>
                </div>
                <div className="blog-card-body">
                  {getLocalizedField(featured, "categoryName", lang) && <span className="blog-card-category" style={{ alignSelf: "flex-start" }}>{getLocalizedField(featured, "categoryName", lang)}</span>}
                  <div className="blog-card-title" style={{ fontSize: 26, lineHeight: 1.3 }}>
                    {getLocalizedField(featured, "title", lang) || ""}
                  </div>
                  <div className="blog-card-excerpt" style={{ fontSize: 15 }}>
                    {getLocalizedField(featured, "summary", lang) || ""}
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 16,
                    paddingTop: 16,
                    borderTop: "1px solid var(--color-border)",
                  }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--color-brand), var(--color-brand-dark))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontWeight: 700,
                    }}>G</div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>GuineeMall</div>
                      <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                        {formatDate(featured?.publishTime)}
                      </div>
                    </div>
                    <span style={{ marginLeft: "auto", fontSize: 14, color: "var(--color-text-muted)" }}>
                      👁 {formatViewCount(featured?.viewCount)} {t("blog.viewUnit")}
                    </span>
                  </div>
                </div>
              </article>
            </Link>

            <div className="flex flex-col gap-4" style={{ height: "100%" }}>
              {sidebarArticles.map((s) => (
                <Link key={s.id} href={`/blog/${s.id}`} style={{ textDecoration: "none", flex: 1, minHeight: 0, display: "flex" }}>
                  <article className="blog-card" style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                    <div className="blog-card-image" style={{ aspectRatio: "16/7", flexShrink: 0 }}>
                      <img src={s.coverImage} alt={getLocalizedField(s, "title", lang)}/>
                    </div>
                    <div className="blog-card-body" style={{ padding: "12px 16px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                      <span className="blog-card-category" style={{ fontSize: 10, padding: "2px 8px", marginBottom: 8, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", alignSelf: "flex-start" }}>{getLocalizedField(s, "categoryName", lang)}</span>
                      <div className="blog-card-title" style={{ fontSize: 15, lineHeight: 1.35, marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", flexShrink: 0 }}>{getLocalizedField(s, "title", lang)}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--color-text-secondary)", marginBottom: 6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", flex: 1, minHeight: 0 }}>{getLocalizedField(s, "summary", lang)}</div>
                      <div className="blog-card-meta" style={{ flexShrink: 0 }}>
                        <span>📅 {formatDate(s.publishTime)}</span>
                        <span>👁 {formatViewCount(s.viewCount)}</span>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>

    {/* ---------- All Articles ---------- */}
    <div className="container">
      <div className="section">
        {/* section-header stays visible — no skeleton */}
        <div className="section-header" style={{ marginBottom: 16 }}>
          <div>
            <div className="section-title-eyebrow">{t("blog.sectionAllArticlesEyebrow")}</div>
            <h2 className="section-title">{t("blog.sectionLatest")}</h2>
          </div>
        </div>

        {catLoading ? (
          <FilterBarSkeleton />
        ) : (
          <div className="filter-bar" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)", paddingLeft: 20, paddingRight: 20, marginBottom: 24 }}>
            <span style={{ fontSize: 14, color: "var(--color-text-secondary)", marginRight: 8 }}>{t("blog.categoryLabel")}</span>
            <button
              className={`filter-chip ${categoryId === null ? "active" : ""}`}
              onClick={() => { setCategoryId(null); setPageNum(1); }}
            >{t("blog.all")}</button>
            {categories.map((c) => (
              <button
                key={c.id}
                className={`filter-chip ${categoryId === c.id ? "active" : ""}`}
                onClick={() => { setCategoryId(c.id); setPageNum(1); }}
              >{getLocalizedField(c, "categoryName", lang)}</button>
            ))}
            <div className="filter-sort">
              <select
                value={sort}
                onChange={e => { setSort(e.target.value); setPageNum(1); }}
              >
                <option value="time">{t("blog.sortNewest")}</option>
                <option value="hot">{t("blog.sortHottest")}</option>
              </select>
            </div>
          </div>
        )}

        {listLoading ? (
          <div className="grid grid-cols-3" style={{ gap: "var(--space-5)" }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <ArticleCardSkeleton key={i} />
            ))}
          </div>
        ) : records.length === 0 ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: "var(--color-text-muted)" }}>
            <div style={{ fontSize: 14, opacity: 0.5 }}>{t("blog.noArticles")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-3" style={{ gap: "var(--space-5)" }}>
            {records.map((a) => (
              <Link key={a.id} href={`/blog/${a.id}`} className="blog-card">
                <div className="blog-card-image">
                  <img src={a.coverImage} alt={getLocalizedField(a, "title", lang)}/>
                </div>
                <div className="blog-card-body">
                  <span className="blog-card-category" style={{ alignSelf: "flex-start" }}>{getLocalizedField(a, "categoryName", lang)}</span>
                  <div className="blog-card-title">{getLocalizedField(a, "title", lang)}</div>
                  <div className="blog-card-excerpt">{getLocalizedField(a, "summary", lang)}</div>
                  <div className="blog-card-meta">
                    <span>📅 {formatDate(a.publishTime)}</span>
                    <span>👁 {formatViewCount(a.viewCount)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination mt-8 mb-12">
            <button disabled={pageNum <= 1} onClick={() => setPageNum(1)}>«</button>
            <button disabled={pageNum <= 1} onClick={() => setPageNum(prev => prev - 1)}>‹</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 5) p = i + 1;
              else if (pageNum <= 3) p = i + 1;
              else if (pageNum >= totalPages - 2) p = totalPages - 4 + i;
              else p = pageNum - 2 + i;
              return (
                <button key={p} className={pageNum === p ? "active" : ""} onClick={() => setPageNum(p)}>{p}</button>
              );
            })}
            <button disabled={pageNum >= totalPages} onClick={() => setPageNum(prev => prev + 1)}>›</button>
            <button disabled={pageNum >= totalPages} onClick={() => setPageNum(totalPages)}>»</button>
          </div>
        )}
      </div>
    </div>
  </>);
}
